import type { ReactNode } from "react";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { cn } from "../lib/utils";
import { useTranslation } from "../i18n";

const emptyOptionValue = "__nexra_select_empty__";

export type SelectOption = Readonly<{
  value: string;
  label: string;
  icon?: ReactNode;
  hint?: string;
  disabled?: boolean;
}>;

export function SelectField({
  value,
  onValueChange,
  options,
  placeholder,
  disabled,
  id,
  name,
  required,
  "aria-label": ariaLabel,
  className,
  size = "default",
}: Readonly<{
  value: string;
  onValueChange(value: string): void;
  options: ReadonlyArray<SelectOption>;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  required?: boolean;
  "aria-label"?: string;
  className?: string;
  size?: "sm" | "default";
}>) {
  const { t } = useTranslation();
  const selected = options.find((option) => option.value === value);
  const hasEmptyOption = options.some((option) => option.value === "");
  // An empty string keeps the select controlled while showing its placeholder.
  const radixValue = value === "" && hasEmptyOption ? emptyOptionValue : value;
  return (
    <Select disabled={disabled} name={name} onValueChange={(next) => onValueChange(next === emptyOptionValue ? "" : next)} required={required} value={radixValue}>
      <SelectTrigger aria-label={ariaLabel} className={cn("w-full", className)} id={id} size={size}>
        {selected ? (
          <span className="flex min-w-0 items-center gap-2">
            {selected.icon}
            <span className="truncate">{selected.label}</span>
          </span>
        ) : (
          <SelectValue placeholder={placeholder ?? t("common.selectPlaceholder")} />
        )}
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem disabled={option.disabled} key={option.value} value={option.value || emptyOptionValue}>
            {option.hint ? (
              <span className="flex min-w-0 flex-col items-start gap-0.5">
                <span className="flex min-w-0 items-center gap-2">
                  {option.icon}
                  <span className="truncate">{option.label}</span>
                </span>
                <span className="font-mono text-xs font-normal text-zinc-500">{option.hint}</span>
              </span>
            ) : (
              <span className="flex min-w-0 items-center gap-2">
                {option.icon}
                <span className="truncate">{option.label}</span>
              </span>
            )}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
